"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useCallback, useEffect, useMemo, useState } from "react"
import type {
  ConsumptionHistoryResponse,
  DayLedgerResponse,
  MealInstance,
  PlanProposalRequest,
  PlanResponse,
  Profile,
  PreparedBatchesResponse,
} from "@bitebalance/contracts"
import { ApiError } from "@bitebalance/contracts/client"
import { useAmyDataRefresh } from "@/lib/useAmyDataRefresh"
import { useApiClient } from "@/api/useApiClient"
import { historyRangeForPlan } from "@/api/consumptionHistory"
import { useI18n } from "@/i18n"
import { LoadingState, EmptyState, ErrorState } from "@/components/state"
import { WeekGrid } from "@/components/plan/WeekGrid"
import { RegenFailedBanner } from "@/components/plan/RegenFailedBanner"
import { WeekEndedBanner } from "@/components/plan/WeekEndedBanner"
import { planCoversDate } from "@/lib/planWeek"
import { SettingsDriftNotice } from "@/components/plan/SettingsDriftNotice"
import { ConstraintConflictBanner } from "@/components/plan/ConstraintConflictBanner"
import { BatchCard } from "@/components/plan/BatchCard"
import { isPreparable, preparedFor } from "@/components/plan/preparedView"
import { SwapProposalSheet } from "@/components/plan/SwapProposalSheet"
import { OutcomeSheet } from "@/components/plan/OutcomeSheet"
import { planningModeLabel } from "@/components/plan/recipeDisplay"
import {
  consumptionKey,
  indexOutcomeFacts,
  mealOutcomeExtras,
  outcomeFactsFromRecord,
  outcomeFactsFromTodayLedger,
  type MealOutcomeExtras,
} from "@/components/plan/outcomeDisplay"
import { Button } from "@/components/ui/button"

type LoadStatus = "loading" | "success" | "error"

/** Plan (T12): week grid, batch summary, and the swap/apply/undo flow. */
export default function PlanPage() {
  const { t, locale } = useI18n()
  const api = useApiClient()
  const [status, setStatus] = useState<LoadStatus>("loading")
  const [plan, setPlan] = useState<PlanResponse | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [staleBanner, setStaleBanner] = useState(false)
  // ADR-065 Prepared Food Inventory: what the user confirmed they cooked.
  // A quiet enhancement — a failed read shows no prepared state, never an error.
  const [prepared, setPrepared] = useState<PreparedBatchesResponse | null>(null)
  const [markingBatchKey, setMarkingBatchKey] = useState<string | null>(null)
  const [preparedError, setPreparedError] = useState(false)

  const [proposalMeal, setProposalMeal] = useState<MealInstance | null>(null)
  const [proposalIntent, setProposalIntent] = useState<PlanProposalRequest["intent"]>("swap")
  const [outcomeMeal, setOutcomeMeal] = useState<MealInstance | null>(null)

  const [canUndo, setCanUndo] = useState(false)
  const [undoStatus, setUndoStatus] = useState<"idle" | "undoing" | "done" | "error">("idle")

  const [generating, setGenerating] = useState(false)

  const load = useCallback(
    (quiet = false) => {
      if (!quiet) {
        setStatus("loading")
        setStaleBanner(false)
      }
      Promise.all([api.getCurrentPlan(), api.getProfile()])
        .then(([planRes, profileRes]) => {
          setPlan(planRes)
          setProfile(profileRes as unknown as Profile)
          setStatus("success")
        })
        .catch(() => {
          if (!quiet) setStatus("error")
        })
      api
        .getPreparedBatches()
        .then(setPrepared)
        .catch(() => setPrepared(null))
    },
    [api]
  )

  /**
   * "I cooked this batch." The server deducts the raw ingredients it can and
   * keeps the portions; we re-read rather than guessing the new balance.
   */
  async function markPrepared(batchKey: string) {
    if (!plan) return
    setMarkingBatchKey(batchKey)
    setPreparedError(false)
    try {
      await api.prepareBatch({ batchKey, baseRevision: plan.revision })
      setPrepared(await api.getPreparedBatches())
    } catch {
      setPreparedError(true)
    } finally {
      setMarkingBatchKey(null)
    }
  }

  // ADR-036 finding 6: Amy's rebuilds and edits land here without a manual
  // reload — quiet refresh, no spinner flash over a rendered week.
  // 2026-09-03 consistency finding: Plan consumes the SAME authoritative
  // day ledger Today does — today's meal cards render persisted outcomes
  // and can never disagree with Today after a reload.
  const [ledger, setLedger] = useState<DayLedgerResponse | null>(null)
  const [ledgerNudge, setLedgerNudge] = useState(0)
  useEffect(() => {
    let cancelled = false
    api
      .getDayLedger()
      .then(l => {
        if (!cancelled) setLedger(l)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [api, ledgerNudge])

  // ADR-056 (founder finding 2026-09-05: yesterday's logged meals
  // "disappeared" this morning): outcome marks come from IMMUTABLE
  // consumption history keyed by (meal date, slot) — never from a wire
  // meal id, which every plan revision re-issues. The range covers every
  // date the grid can show and is re-read together with the ledger after
  // an outcome is recorded or Amy changes something — but NOT on a plan
  // revision: history is independent of revisions, which is the point.
  const [history, setHistory] = useState<ConsumptionHistoryResponse | null>(null)
  const historyRange = useMemo(() => (plan ? historyRangeForPlan(plan) : null), [plan])
  const historyFrom = historyRange?.from ?? null
  const historyTo = historyRange?.to ?? null
  useEffect(() => {
    if (!historyFrom || !historyTo) return
    let cancelled = false
    api
      .getConsumptionHistory({ from: historyFrom, to: historyTo })
      .then(h => {
        if (!cancelled) setHistory(h)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [api, historyFrom, historyTo, ledgerNudge])

  useAmyDataRefresh(() => {
    load(true)
    setLedgerNudge(n => n + 1)
  })

  // Recovery path (found live 2026-08-23): an onboarded user whose first
  // plan job failed had NO way to try again — the error state only
  // reloaded. This queues a fresh generation job and polls for the plan.
  const generatePlan = useCallback(async () => {
    setGenerating(true)
    try {
      await api.createPlanJob({ trigger: "manual" })
      for (let attempt = 0; attempt < 15; attempt++) {
        await new Promise(resolve => setTimeout(resolve, 2000))
        try {
          const planRes = await api.getCurrentPlan()
          const profileRes = await api.getProfile()
          setPlan(planRes)
          setProfile(profileRes as unknown as Profile)
          setStatus("success")
          return
        } catch {
          // plan not ready yet — keep polling
        }
      }
      setStatus("error")
    } catch {
      setStatus("error")
    } finally {
      setGenerating(false)
    }
  }, [api])

  useEffect(() => {
    load()
  }, [load])

  function openProposal(meal: MealInstance, intent: PlanProposalRequest["intent"]) {
    setProposalMeal(meal)
    setProposalIntent(intent)
  }

  async function handleUndo() {
    if (!plan) return
    setUndoStatus("undoing")
    try {
      const result = await api.undoPlan(plan.id, { baseRevision: plan.revision })
      setPlan(result.plan)
      setCanUndo(false)
      setUndoStatus("done")
    } catch (err) {
      if (err instanceof ApiError && err.envelope.code === "STALE_REVISION") {
        setStaleBanner(true)
        setUndoStatus("idle")
        return
      }
      setUndoStatus("error")
    }
  }

  if (status === "loading") return <LoadingState />
  if (generating) return <LoadingState label={t("views.plan.generating")} />
  if (status === "error" || !plan || !profile)
    return (
      <div className="flex flex-col items-center gap-4">
        <ErrorState message={t("views.plan.noPlanYetBody")} onRetry={load} />
        <Button type="button" className="min-h-11" onClick={() => void generatePlan()}>
          {t("views.plan.generateButton")}
        </Button>
      </div>
    )

  // P1 (founder-reported, 2026-09-22): a real plan whose week has ENDED —
  // distinct from a fresh/broken account that never generated a plan at
  // all (the `!plan` early return above, unaffected by this pass).
  const today = new Date().toISOString().slice(0, 10)
  const weekEnded = !planCoversDate(plan.weekStartDate, today)

  const batchGroups = new Map<string, MealInstance[]>()
  for (const meal of plan.meals) {
    if (!meal.batchComponentId) continue
    const group = batchGroups.get(meal.batchComponentId) ?? []
    group.push(meal)
    batchGroups.set(meal.batchComponentId, group)
  }

  // Every card is decorated by its (date, slot) from the canonical records:
  // the history week, then today's ledger (the SAME records, and the
  // freshest source right after an outcome is recorded). The changed-meal
  // note only ever names today's off-plan entries — nothing is invented
  // for a past day.
  const outcomeIndex = indexOutcomeFacts(
    (history?.days ?? []).flatMap(day => day.meals.map(outcomeFactsFromRecord)),
    outcomeFactsFromTodayLedger(ledger)
  )
  const outcomeExtras = new Map<string, MealOutcomeExtras>()
  for (const meal of plan.meals) {
    const extras = mealOutcomeExtras(outcomeIndex, meal, t, locale)
    if (extras) outcomeExtras.set(consumptionKey(meal.date, meal.slot), extras)
  }

  return (
    <section aria-labelledby="plan-heading" className="flex flex-col gap-7">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 id="plan-heading" className="font-display text-3xl font-bold tracking-tight text-ink">
            {t("views.plan.weekTitle")}
          </h1>
          <p className="mt-1 text-sm text-ink-2">{planningModeLabel(profile.planningMode, t)}</p>
        </div>
        {canUndo && (
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            disabled={undoStatus === "undoing"}
            onClick={() => void handleUndo()}
          >
            {undoStatus === "undoing" ? t("views.plan.undoing") : t("views.plan.undoButton")}
          </Button>
        )}
      </div>

      {weekEnded && <WeekEndedBanner onRegenerated={load} />}
      {staleBanner && <ErrorState message={t("views.plan.staleReloadBody")} onRetry={load} />}
      {plan.constraintConflicts && plan.constraintConflicts.length > 0 && (
        <ConstraintConflictBanner
          planId={plan.id}
          baseRevision={plan.revision}
          conflicts={plan.constraintConflicts}
          onApplied={load}
        />
      )}
      {plan.latestJob?.status === "failed" && (
        // The BUDGET_INFEASIBLE branch is driven by the server-derived
        // `applicability` (owner invariant 2026-09-04): a failure produced
        // under superseded settings is never narrated as "your maximum".
        <RegenFailedBanner
          onRegenerated={load}
          failedError={plan.latestJob.failedError}
          applicability={plan.latestJob.applicability}
        />
      )}
      {/* Consumer UX gate: only when days genuinely no longer match the
          target — an in-band target tweak (zero stale days) is not notice-worthy. */}
      {plan.settingsDrift && plan.settingsDrift.staleDayIndexes.length > 0 && (
        <SettingsDriftNotice drift={plan.settingsDrift} />
      )}
      {undoStatus === "done" && (
        <p role="status" className="text-sm text-green">
          {t("views.plan.undoSuccess")}
        </p>
      )}
      {undoStatus === "error" && (
        <p role="alert" className="text-sm text-destructive">
          {t("views.plan.undoError")}
        </p>
      )}

      {plan.meals.length === 0 ? (
        <EmptyState title={t("plan.emptyTitle")} body={t("plan.emptyBody")} />
      ) : (
        <WeekGrid
          meals={plan.meals}
          onSwap={m => openProposal(m, "swap")}
          onMarkOutcome={m => setOutcomeMeal(m)}
          conflictMealIds={new Set((plan.constraintConflicts ?? []).map(c => c.mealId))}
          outcomeExtras={outcomeExtras}
          calorieTarget={profile.calorieTarget?.kcal ?? null}
        />
      )}

      {batchGroups.size > 0 && (
        <div className="flex flex-col gap-3">
          <h2 className="text-xs font-bold tracking-wide text-ink-2 uppercase">
            {t("views.plan.batchSectionTitle")}
          </h2>
          {preparedError && (
            <p role="alert" className="text-sm text-red-700" data-testid="batch-prepared-error">
              {t("views.plan.preparedError")}
            </p>
          )}
          {Array.from(batchGroups.entries()).map(([id, meals]) => (
            <BatchCard
              key={id}
              batchComponentId={id}
              meals={meals}
              prepared={preparedFor(prepared, id)}
              onMarkPrepared={isPreparable(prepared, id) ? () => void markPrepared(id) : undefined}
              marking={markingBatchKey === id}
            />
          ))}
        </div>
      )}

      <SwapProposalSheet
        open={proposalMeal !== null}
        onOpenChange={open => {
          if (!open) setProposalMeal(null)
        }}
        planId={plan.id}
        baseRevision={plan.revision}
        meal={proposalMeal}
        intent={proposalIntent}
        onApplied={result => {
          setPlan(result.plan)
          setCanUndo(true)
          setUndoStatus("idle")
        }}
        onStaleRevision={() => {
          setProposalMeal(null)
          load()
        }}
      />

      <OutcomeSheet
        open={outcomeMeal !== null}
        onOpenChange={open => {
          if (!open) {
            setOutcomeMeal(null)
            // A just-recorded outcome must show on its card immediately —
            // the nudge re-reads BOTH the ledger and the history week.
            setLedgerNudge(n => n + 1)
          }
        }}
        meal={outcomeMeal}
      />
    </section>
  )
}
